import { useDispatch, useSelector } from "react-redux";
import { FiX, FiCheck } from "react-icons/fi";
import { closePlanSelectionModal } from "../redux/dealerSlice";
import type { RootState, AppDispatch } from "../redux/store";


const TIER_ICONS: Record<string, string> = {
    bronze: '/bronze.png',
    silver: '/silver.png',
    gold: '/gold.png',
    platinum: '/platinum.png',
};

const PLANS = [
    {
        id: "bronze",
        name: "Bronze",
        priceMonthly: 0,
        priceAnnual: 0,
        description: "Essential visibility for local dealers",
        color: "gray",
        features: [
            "Basic listing",
            "Map + distance visibility",
            "Backlink required",
            "3 service categories",
            "5 photo uploads max"
        ],
        buttonText: "Current Plan",
        recommended: false,
        gradient: "from-gray-50 to-gray-100",
        border: "border-gray-200"
    },
    {
        id: "silver",
        name: "Silver",
        priceMonthly: 29,
        priceAnnual: 24,
        description: "Standard tools to grow your business",
        color: "blue",
        features: [
            "Everything in Bronze",
            "Unlimited photos",
            "Lead form enabled",
            "Access to leads dashboard"
        ],
        buttonText: "Upgrade to Silver",
        recommended: false,
        gradient: "from-blue-50 to-white",
        border: "border-blue-100"
    },
    {
        id: "gold",
        name: "Gold",
        priceMonthly: 79,
        priceAnnual: 64,
        description: "Premium exposure and advanced tools",
        color: "yellow",
        features: [
            "Everything in Silver",
            "Video uploads (3 max)",
            "Featured badge",
            "Priority listing on top",
            "“Top Dealer” spot automatically",
            "Advanced Analytics"
        ],
        buttonText: "Upgrade to Gold",
        recommended: false,
        gradient: "from-yellow-50 to-white",
        border: "border-yellow-200"
    },
    {
        id: "platinum",
        name: "Platinum",
        priceMonthly: 149,
        priceAnnual: 119,
        description: "Elite status for industry leaders",
        color: "purple",
        features: [
            "Everything in Gold",
            "Showcase Video section",
            "WhatsApp contact button",
            "Custom banner",
            "Leads tagging + CRM-like dashboard"
        ],
        buttonText: "Go Platinum",
        recommended: true,
        gradient: "from-purple-50 to-white",
        border: "border-purple-200"
    }
];

// BOOST_PLAN removed as part of refactor

export default function PlanSelectionModal() {
    const dispatch = useDispatch<AppDispatch>();
    const { isPlanSelectionModalOpen, profile } = useSelector((state: RootState) => state.dealer);
    // const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("annual");

    const getTierLevel = (id: string, revenue?: number) => {
        // Revenue Priority
        if (revenue !== undefined) {
            if (revenue >= 10000) return 3; // platinum
            if (revenue >= 6000) return 2; // gold
            if (revenue >= 2500) return 1; // silver
            return 0; // bronze
        }

        switch (id) {
            case "platinum": return 3;
            case "gold": return 2;
            case "silver": return 1;
            default: return 0;
        }
    };

    const currentRevenueLevel = profile?.revenue !== undefined ? getTierLevel("", profile.revenue) : -1;
    const currentSubscriptionTier = (profile?.tier && typeof profile.tier === "object") ? profile.tier.name.toLowerCase() : (profile?.tier as string)?.toLowerCase() || "bronze";

    if (!isPlanSelectionModalOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-[#FBFAFA] rounded-3xl shadow-2xl w-full max-w-6xl my-4 animate-in fade-in zoom-in duration-300 overflow-hidden relative">

                {/* Header (Compact) */}
                <div className="p-6 pb-3 flex flex-col md:flex-row items-center justify-between bg-white border-b border-gray-100 relative">
                    <div className="text-center md:text-left mb-4 md:mb-0">
                        <h2 className="text-2xl font-black text-gray-900 leading-tight">
                            Revenue Tiers
                        </h2>
                        <p className="text-xs text-gray-400 font-medium">
                            Unlock exclusive tools and benefits as your business grows.
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Billing Toggle Removed as per request */}

                        {/* Close Button (Inside Flex) */}
                        <button
                            onClick={() => dispatch(closePlanSelectionModal())}
                            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 transition"
                        >
                            <FiX size={20} />
                        </button>
                    </div>
                </div>

                {/* Plans Grid (Comparison View) */}
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 justify-center">
                    {PLANS.map((plan) => {
                        const planLevel = getTierLevel(plan.id);

                        // Hybrid Logic:
                        // Active if:
                        // 1. Revenue authorizes this tier (or higher)
                        // 2. OR User explicitly subscribed to this tier
                        const isRevenueUnlocked = currentRevenueLevel >= planLevel;
                        const isSubscribed = currentSubscriptionTier === plan.id;
                        const isActive = isRevenueUnlocked || isSubscribed;

                        return (
                            <div
                                key={plan.id}
                                className={`relative flex flex-col rounded-3xl border-2 p-4 transition-all duration-300 bg-white
                                    ${plan.recommended ? 'border-[#B08D55] shadow-lg shadow-[#B08D55]/5 ring-1 ring-[#B08D55]/10' : 'border-gray-50 hover:border-[#B08D55]/20'}
                                    ${isActive ? 'bg-gray-50/50 border-gray-200' : 'hover:shadow-xl hover:shadow-gray-100 hover:-translate-y-0.5'}
                                `}
                            >
                                {plan.recommended && (
                                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#B08D55] text-white text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-md">
                                        Best Value
                                    </div>
                                )}

                                <div className="mb-4">
                                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center mb-3 shadow-sm`}>
                                        <img src={TIER_ICONS[plan.id]} alt={plan.name} className="w-6 h-6 object-contain" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-0.5">{plan.name}</h3>
                                    <p className="text-[10px] text-gray-400 mt-1 font-medium leading-tight uppercase tracking-tighter line-clamp-1">{plan.description}</p>
                                </div>

                                <div className="flex-1 space-y-2 mb-6">
                                    {plan.features.map((feature, i) => (
                                        <div key={i} className="flex gap-2 text-[11px] text-gray-600 items-start">
                                            <FiCheck className="text-green-500 flex-shrink-0 mt-0.5" size={10} strokeWidth={5} />
                                            <span className="leading-tight font-medium">{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                <div
                                    className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest text-center
                                        ${isActive
                                            ? 'bg-gray-100 text-gray-400 cursor-default border border-gray-200'
                                            : 'bg-white border-2 border-gray-100 text-gray-400'
                                        }
                                    `}
                                >
                                    {isActive ? (isRevenueUnlocked ? "Earned (Revenue)" : "Active Tier") : "Tier Benefits"}
                                </div>
                            </div>
                        );
                    })}
                </div>


            </div>
        </div>
    );
}
